import { ArrowLeft, Square, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { isIngestJobFinished } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { StatusDot } from '../../../components/primitives/StatusDot'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { cn } from '../../../lib/utils'
import { WriteButton } from '../../IngestPage/shared/WriteButton'
import {
  statusToLabel,
  statusToPill,
  type IngestJobState
} from '../../IngestPage/util/jobStatusMap'

/** Compact h/m/s duration between two instants (now when `endIso` is absent). */
function formatElapsed(startIso?: string, endIso?: string): string {
  if (!startIso) return ''
  const start = Date.parse(startIso)
  if (!Number.isFinite(start)) return ''
  const end = endIso ? Date.parse(endIso) : Date.now()
  if (!Number.isFinite(end)) return ''
  const secs = Math.max(0, Math.floor((end - start) / 1000))
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** Quiet label + monospace value — the spine's "instrument readout" metadata. */
function Meta({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className="text-muted-foreground/60">{label}</span>
      <span className="font-mono text-foreground/85">{value}</span>
    </span>
  )
}

interface RunSpineProps {
  jobId: string
  fileName?: string
  branchId?: string
  /** Human-readable branch name; falls back to the short id when unresolved. */
  branchName?: string
  state: IngestJobState
  submittedAt?: string
  updatedAt?: string
  /** Number of open `user_question` rows — drives the "needs input" pill. */
  pendingQuestionCount: number
  /** The latest job-log line, shown as the one-line "current action". */
  currentAction?: string
  /** Open the Add documents modal. */
  onAddDocuments: () => void
  /** Server-target-aware label for the document action. */
  documentActionLabel: string
  /** Whether a document transfer is already open (disables the button). */
  documentTransferOpen?: boolean
  /** Scroll to / open the commit gate. Only meaningful once the run is done. */
  onReview: () => void
  /** Jump the Current Ingest rail to the parked question. */
  onNeedsInput?: () => void
  /** Stop the run, keeping its branch. Shown until the job finishes. */
  onStop: () => void
  /** Disables Stop while the request is in flight. */
  stopping?: boolean
  /** Open the delete-job confirm dialog. Delete is hidden without it. */
  onDelete?: () => void
  /** Without `ingest/manage`, Stop and Delete stay visible but disabled with the reason. */
  canManage: boolean
}

/**
 * Sticky status console for the agent-ingest page: the run's pill, the corpus
 * file name as the page title, an instrument strip (branch / job / elapsed), a
 * live "current action" readout (the latest log line, with a pulsing status LED
 * while the run is active), and the Review & commit action. The pill flips to a
 * warn "needs input" state whenever a `user_question` is pending — the same
 * durable signal the conversation reads — since the agent path never moves the
 * job out of `processing` while parked.
 */
export function RunSpine({
  jobId,
  fileName,
  branchId,
  branchName,
  state,
  submittedAt,
  updatedAt,
  pendingQuestionCount,
  currentAction,
  onAddDocuments,
  documentActionLabel,
  documentTransferOpen = false,
  onReview,
  onNeedsInput,
  onStop,
  stopping = false,
  onDelete,
  canManage
}: RunSpineProps) {
  const isTerminal = isIngestJobFinished(state)
  const active = !isTerminal
  const needsInput = pendingQuestionCount > 0 && active
  const pillStatus = needsInput ? 'warn' : statusToPill[state]
  const pillLabel = needsInput ? 'needs input' : statusToLabel[state]
  const elapsed = formatElapsed(submittedAt, isTerminal ? updatedAt : undefined)
  const canReview = state === 'done'
  return (
    <div className="sticky top-12 z-[5] -mt-4 border-b border-border bg-background py-3 md:-mt-6">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="-ml-2 h-auto w-fit px-2 py-1 text-xs text-muted-foreground"
      >
        <Link to="/ingest">
          <ArrowLeft className="size-3.5" />
          Back to ingest
        </Link>
      </Button>

      <div className="mt-1.5 flex items-center gap-3">
        {needsInput && onNeedsInput ? (
          <button
            type="button"
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-st-warn/40"
            onClick={onNeedsInput}
          >
            <StatusPill status={pillStatus}>{pillLabel}</StatusPill>
          </button>
        ) : (
          <StatusPill status={pillStatus}>{pillLabel}</StatusPill>
        )}
        <h1
          className="min-w-0 flex-1 truncate text-lg font-semibold tracking-tight text-foreground"
          title={fileName}
        >
          {fileName ?? 'Agent ingest'}
        </h1>
        <div className="flex items-center gap-2">
          {active ? (
            <WriteButton
              canManage={canManage}
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              title="Stop this ingest job and keep the branch"
              onClick={onStop}
              disabled={stopping}
            >
              <Square className="size-3 fill-current" />
              {stopping ? 'Stopping…' : 'Stop ingest'}
            </WriteButton>
          ) : null}
          {onDelete ? (
            <WriteButton
              canManage={canManage}
              size="sm"
              variant="outline"
              className="border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
              title="Delete this ingest job"
              onClick={onDelete}
            >
              <Trash2 className="size-3.5" />
              Delete
            </WriteButton>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={onAddDocuments}
            disabled={documentTransferOpen}
          >
            {documentActionLabel}
          </Button>
          <Button
            size="sm"
            variant={canReview ? 'default' : 'outline'}
            onClick={onReview}
            disabled={!canReview}
            title={canReview ? 'Review the branch and merge' : 'Available once the run finishes'}
          >
            Review &amp; merge
          </Button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {branchId ? (
          <Meta label="branch" value={branchName ?? branchId.slice(0, 8)} title={branchId} />
        ) : null}
        <span className="text-border" aria-hidden>
          ·
        </span>
        <Meta label="job" value={jobId.slice(0, 8)} title={jobId} />
        {elapsed ? (
          <>
            <span className="text-border" aria-hidden>
              ·
            </span>
            <Meta label="elapsed" value={elapsed} />
          </>
        ) : null}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <StatusDot
          status={pillStatus}
          className={cn(active && '[animation:status-pulse_1.4s_ease-in-out_infinite]')}
        />
        <span
          className="min-w-0 truncate font-mono text-xs text-muted-foreground"
          title={currentAction}
        >
          {currentAction ?? 'Waiting for the agent to start…'}
        </span>
      </div>
    </div>
  )
}

export default RunSpine
